import React from 'react';
import clsx from 'clsx';
import { twMerge } from '@/utilities';

export type ToggleProps = {
  className?: string;
  disabled?: boolean;
  value?: boolean;
  onChange?: (newValue: boolean) => void;
  label?: string;
  labelPosition?: 'left' | 'right';
  labelClass?: string;
  variant?: 'switch' | 'checkbox';
  checkboxClassName?: string;
  transitionColor?: boolean;
};

export const Toggle: React.FC<ToggleProps> = ({
  disabled,
  value = false,
  onChange,
  label,
  className,
  labelPosition = 'left',
  labelClass = 'text-text text-medium',
  variant = 'switch',
  checkboxClassName,
  transitionColor = true,
}) => {
  const positionClass =
    labelPosition === 'left' ? 'flex-row-reverse justify-end' : 'flex-row';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onChange?.(e.target.checked);
  };

  const defaultCheckboxStyles =
    'appearance-none w-8 h-8 rounded border border-active bg-content checked:bg-main checked:border-main focus:ring focus:ring-main/20 transition-colors duration-150 cursor-pointer checkbox-simple';

  return (
    <div
      className={twMerge(
        'flex items-center gap-2 justify-end',
        positionClass,
        className
      )}
    >
      {label && (
        <span
          className={clsx(
            labelClass,
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          {label}
        </span>
      )}

      {variant === 'checkbox' ? (
        <input
          type="checkbox"
          checked={value}
          onChange={handleChange}
          disabled={disabled}
          className={clsx(
            checkboxClassName || defaultCheckboxStyles,
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        />
      ) : (
        <input
          type="checkbox"
          checked={value}
          onChange={handleChange}
          className={clsx(
            transitionColor && 'transition-colors duration-200',
            disabled && 'opacity-40 pointer-events-none',

            // Track
            'relative appearance-none w-16 h-8 rounded-full',
            'border border-border bg-content cursor-pointer',
            'checked:bg-main',

            // Handle
            'before:content-[""] before:absolute',
            'before:top-1/2 before:left-[2px]',
            'before:-translate-y-1/2',
            'before:w-6 before:h-6',
            'before:rounded-full before:bg-main',
            'checked:before:bg-content',

            // Move handle
            'before:translate-x-0 checked:before:translate-x-8',

            'before:transition-all before:duration-200'
          )}
        />
      )}
    </div>
  );
};

export default React.memo(Toggle) as typeof Toggle;
